import { useEffect, useMemo, useState } from 'react';
import { ApiError, errorMessage, patch } from '../../api/client';
import type { BikeModel, Media } from '../../api/types';
import { ImageSlot } from '../../components/media';
import { Field, slugify, useFeedback } from '../../components/ui';
import { SaveBar } from './SaveBar';
import { useDraft, useUnloadGuard } from '../../hooks';
import { useT } from '../../i18n';
import { LIMITS, numberText, parseNumber, type TabProps } from './shared';

interface Form {
  name: string;
  slug: string;
  headline: string;
  subtitle: string;
  category: string;
  seoDescription: string;
  heroAlt: string;
  showPrice: boolean;
  price: string;
  currency: string;
  priceNote: string;
  statCc: string;
  statKw: string;
  statKmh: string;
  statKg: string;
  heroMedia: Media | null;
  heroBlurMedia: Media | null;
}

const fromModel = (m: BikeModel): Form => ({
  name: m.name,
  slug: m.slug,
  headline: m.headline,
  subtitle: m.subtitle,
  category: m.category,
  seoDescription: m.seoDescription,
  heroAlt: m.heroAlt,
  showPrice: m.price !== null,
  price: m.price === null ? '' : String(m.price),
  currency: m.currency,
  priceNote: m.priceNote,
  statCc: numberText(m.statCc),
  statKw: numberText(m.statKw),
  statKmh: numberText(m.statKmh),
  statKg: numberText(m.statKg),
  heroMedia: m.heroMedia,
  heroBlurMedia: m.heroBlurMedia,
});

const CURRENCIES = ['USD', 'NIO', 'COP', 'MXN', 'EUR'];

/** Formato de las cifras como en el sitio ("8,4KW"). */
const stat = (v: string, unit: string) => (v ? `${v.replace('.', ',')}${unit}` : '—');

export function GeneralTab({ model, onSaved, onDirty }: TabProps) {
  const { t } = useT();
  const initial = useMemo(() => fromModel(model), [model]);
  const { draft: f, setDraft: setF, dirty, reset, markSaved } = useDraft<Form>(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const { toast } = useFeedback();
  useUnloadGuard(dirty);
  useEffect(() => onDirty(dirty), [dirty, onDirty]);

  const set = <K extends keyof Form>(key: K, value: Form[K]) => setF((prev) => ({ ...prev, [key]: value }));

  const save = async () => {
    const submitted = f;
    const numbers = { statCc: parseNumber(f.statCc), statKw: parseNumber(f.statKw), statKmh: parseNumber(f.statKmh), statKg: parseNumber(f.statKg) };
    const price = f.showPrice ? parseNumber(f.price.replace(/\s/g, '')) : null;
    if (Object.values(numbers).some((n) => Number.isNaN(n)) || Number.isNaN(price)) {
      setError(new ApiError(400, t('Revisa las cifras y el precio: solo números, con coma o punto para los decimales y sin puntos de miles')));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { model: saved } = await patch<{ model: BikeModel }>(`/admin/models/${model.id}`, {
        name: f.name.trim().toUpperCase(),
        slug: f.slug,
        headline: f.headline,
        subtitle: f.subtitle,
        category: f.category,
        seoDescription: f.seoDescription,
        heroAlt: f.heroAlt,
        price,
        currency: f.currency,
        priceNote: f.priceNote,
        statCc: numbers.statCc === null ? null : Math.round(numbers.statCc),
        statKw: numbers.statKw,
        statKmh: numbers.statKmh === null ? null : Math.round(numbers.statKmh),
        statKg: numbers.statKg,
        heroMediaId: f.heroMedia?.id ?? null,
        heroBlurMediaId: f.heroBlurMedia?.id ?? null,
      });
      onSaved(saved);
      markSaved(submitted);
      toast(t('Datos generales guardados'));
    } catch (e) {
      setError(e instanceof ApiError ? e : new ApiError(0, errorMessage(e)));
    } finally {
      setBusy(false);
    }
  };

  const text = (key: keyof Form, label: string, max: number, hint?: string, multiline = false) => (
    <Field label={label} hint={hint} error={error?.field(key)} count={{ value: String(f[key]), max }}>
      {(id) =>
        multiline ? (
          <textarea id={id} className="textarea" rows={3} value={String(f[key])} onChange={(e) => set(key, e.target.value as never)} />
        ) : (
          <input id={id} className="input" value={String(f[key])} onChange={(e) => set(key, e.target.value as never)} />
        )
      }
    </Field>
  );

  const number = (key: 'statCc' | 'statKw' | 'statKmh' | 'statKg', label: string, unit: string) => (
    <Field label={label} error={error?.field(key)}>
      {(id) => (
        <div className="input-affix">
          <input id={id} className="input" inputMode="decimal" value={f[key]} onChange={(e) => set(key, e.target.value)} />
          <span className="affix">{unit}</span>
        </div>
      )}
    </Field>
  );

  return (
    <div className="stack">
      <section className="panel panel-pad stack">
        <h2 className="section-title">{t('Nombre y textos')}</h2>
        <div className="grid grid-2">
          <Field label={t('Nombre')} hint={t('En mayúsculas, como en el diseño (RUSH 150)')} error={error?.field('name')} count={{ value: f.name, max: LIMITS.name }}>
            {(id) => <input id={id} className="input" value={f.name} onChange={(e) => set('name', e.target.value.toUpperCase())} />}
          </Field>
          <Field
            label={t('Dirección en el sitio')}
            hint={t('{path} — si la cambias, el enlace viejo deja de existir', { path: `/productos/${f.slug}/` })}
            error={error?.field('slug')}
          >
            {(id) => <input id={id} className="input mono" value={f.slug} onChange={(e) => set('slug', slugify(e.target.value))} />}
          </Field>
          {text('headline', t('Titular'), LIMITS.headline, t('Va en el título de la pestaña: «MTX Rush 150 — Domina cada curva»'))}
          {text('category', t('Categoría'), LIMITS.category, t('Aparece en el catálogo, p. ej. «Street · Naked · 149 cc»'))}
        </div>
        {text('subtitle', t('Subtítulo'), LIMITS.subtitle, t('Va debajo del claim, antes de la galería'))}
        <div className="grid grid-2">
          {text('seoDescription', t('Descripción para Google'), LIMITS.seoDescription, t('Hasta 160 caracteres para que Google no la corte'), true)}
          {text('heroAlt', t('Descripción de la foto principal'), LIMITS.heroAlt, t('Para lectores de pantalla y Google: qué se ve en la foto'), true)}
        </div>
      </section>

      <section className="panel panel-pad stack">
        <div className="row between">
          <h2 className="section-title">{t('Precio')}</h2>
          <label className="switch">
            <input type="checkbox" checked={f.showPrice} onChange={(e) => set('showPrice', e.target.checked)} />
            <span>{t('Mostrar precio en la ficha')}</span>
          </label>
        </div>
        {f.showPrice ? (
          <div className="grid grid-3">
            <Field label={t('Precio')} error={error?.field('price')} hint={t('Sin puntos de miles: 6000000 o 1500,50')}>
              {(id) => <input id={id} className="input" inputMode="decimal" value={f.price} onChange={(e) => set('price', e.target.value)} />}
            </Field>
            <Field label={t('Moneda')}>
              {(id) => (
                <select id={id} className="select" value={f.currency} onChange={(e) => set('currency', e.target.value)}>
                  {[...new Set([f.currency, ...CURRENCIES])].map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
              )}
            </Field>
            {text('priceNote', t('Nota bajo el precio'), LIMITS.priceNote)}
          </div>
        ) : (
          <p className="muted" style={{ margin: 0 }}>
            {t('Sin precio, la ficha no muestra ese bloque.')}
          </p>
        )}
      </section>

      <section className="panel panel-pad stack">
        <div>
          <h2 className="section-title">{t('Cifras')}</h2>
          <p className="sub" style={{ margin: 0 }}>
            {t('Van en el carrusel del home, el catálogo y la ficha. Vista previa: {preview}', {
              preview: [stat(f.statCc, ' CC'), stat(f.statKw, 'KW'), stat(f.statKmh, 'KM/H'), stat(f.statKg, 'KG')].join(' · '),
            })}
          </p>
        </div>
        <div className="grid grid-4">
          {number('statCc', t('Cilindrada'), 'cc')}
          {number('statKw', t('Potencia máxima'), 'kW')}
          {number('statKmh', t('Velocidad máxima'), 'km/h')}
          {number('statKg', t('Peso'), 'kg')}
        </div>
      </section>

      <section className="panel panel-pad stack">
        <h2 className="section-title">{t('Fotos del encabezado')}</h2>
        <div className="grid grid-2">
          <ImageSlot
            label={t('Foto principal')}
            media={f.heroMedia}
            onChange={(m) => set('heroMedia', m)}
            aspect="16 / 9"
            hint={t('Horizontal, al menos 2560 px de ancho (el diseño usa 3840×2161).')}
          />
          <ImageSlot
            label={t('Placa de desenfoque (opcional)')}
            media={f.heroBlurMedia}
            onChange={(m) => set('heroBlurMedia', m)}
            aspect="16 / 9"
            hint={t('Versión desenfocada para los triángulos del encabezado. Sin ella, el sitio desenfoca la foto principal.')}
          />
        </div>
      </section>

      <SaveBar dirty={dirty} busy={busy} error={error} onSave={save} onReset={reset} />
    </div>
  );
}
